// src/components/admin/ReviewsManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  adminFetchReviews,
  adminSendReviewLink,
  adminApproveReview,
  adminRejectReview,
  adminDeleteReview,
} from '../../services/reviewsAPI';

// ── Status badge colours ────────────────────────────────────────────────────
const STATUS_STYLE = {
  pending:  { bg: '#ffc10720', color: '#856404', border: '#ffc10740' },
  approved: { bg: '#28a74520', color: '#28a745', border: '#28a74540' },
  rejected: { bg: '#dc354520', color: '#dc3545', border: '#dc354540' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      textTransform: 'capitalize', whiteSpace: 'nowrap',
    }}>
      {status}
    </span>
  );
};

// ── Star row ────────────────────────────────────────────────────────────────
const Stars = ({ rating }) => (
  <span style={{ color: '#FFD60A', fontSize: 13, letterSpacing: 1 }}>
    {'★'.repeat(rating || 0)}{'☆'.repeat(5 - (rating || 0))}
    <span style={{ color: '#666', fontSize: 11, marginLeft: 4 }}>({rating || '—'})</span>
  </span>
);

// ── Send Link Modal ─────────────────────────────────────────────────────────
const SendLinkModal = ({ onClose, onSent }) => {
  const [form, setForm]   = useState({ customer_name: '', customer_phone: '', review_type: 'buyer', car_id: '' });
  const [busy, setBusy]   = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSend = async () => {
    if (!form.customer_phone.match(/^[0-9]{10}$/)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    setBusy(true); setError('');
    try {
      const data = await adminSendReviewLink(form);
      setResult(data);
      onSent && onSent();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 500 }}>
        {/* Header */}
        <div style={modalHeaderStyle}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#003566' }}>
            📲 Send Review Link via WhatsApp
          </h2>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        <div style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Success view */}
          {result ? (
            <div>
              <div style={{
                background: '#28a74510', border: '2px solid #28a74530',
                borderRadius: 10, padding: 18, marginBottom: 16, textAlign: 'center'
              }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#28a745', marginBottom: 4 }}>
                  Review link created!
                </div>
                <div style={{ fontSize: 12, color: '#555' }}>
                  Click the WhatsApp button below to send it to the customer.
                </div>
              </div>

              {/* Review URL (copyable) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#666', marginBottom: 6, textTransform: 'uppercase' }}>
                  Review Form URL
                </div>
                <div style={{
                  background: '#f5f5f5', border: '1px solid #ddd',
                  borderRadius: 8, padding: '8px 12px',
                  fontSize: 12, wordBreak: 'break-all', color: '#003566', fontWeight: 600
                }}>
                  {result.review_url}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(result.review_url)}
                  style={{ ...secondaryBtn, marginTop: 8, fontSize: 11 }}
                >
                  📋 Copy Link
                </button>
              </div>

              {/* WhatsApp open button */}
              <a
                href={result.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: '#25D366', color: '#fff',
                  padding: '12px 20px', borderRadius: 10, fontWeight: 700, fontSize: 14,
                  textDecoration: 'none', marginBottom: 12,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Open WhatsApp to Send
              </a>

              <button onClick={onClose} style={secondaryBtn}>Done</button>
            </div>
          ) : (
            /* Form view */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ background: '#dc354515', border: '1px solid #dc354530', borderRadius: 8, padding: '10px 14px', color: '#dc3545', fontSize: 13 }}>
                  ⚠️ {error}
                </div>
              )}

              <div>
                <label style={labelStyle}>Customer Name *</label>
                <input
                  style={inputStyle} name="customer_name" placeholder="e.g. Rahul Sharma"
                  value={form.customer_name} onChange={handleChange}
                />
              </div>
              <div>
                <label style={labelStyle}>WhatsApp Number (10 digits) *</label>
                <input
                  style={inputStyle} name="customer_phone" placeholder="9876543210"
                  value={form.customer_phone} onChange={handleChange} maxLength={10}
                />
              </div>
              <div>
                <label style={labelStyle}>Review Type *</label>
                <select style={inputStyle} name="review_type" value={form.review_type} onChange={handleChange}>
                  <option value="buyer">From Buyer</option>
                  <option value="seller">From Seller</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Car ID (optional)</label>
                <input
                  style={inputStyle} name="car_id" placeholder="Leave blank if general review"
                  value={form.car_id} onChange={handleChange}
                />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  If provided, the review will be linked to that car listing.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={onClose} style={secondaryBtn} disabled={busy}>Cancel</button>
                <button onClick={handleSend} style={{ ...primaryBtn, opacity: busy ? 0.7 : 1 }} disabled={busy}>
                  {busy ? 'Creating…' : '📲 Generate & Send Link'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Review Detail / Approve Modal ───────────────────────────────────────────
const ReviewDetailModal = ({ review, onClose, onAction }) => {
  const [note, setNote]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async () => {
    setBusy(true); setError('');
    try {
      await adminApproveReview(review.id, note);
      onAction();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const handleReject = async () => {
    setBusy(true); setError('');
    try {
      await adminRejectReview(review.id, note);
      onAction();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const CATEGORY_LABELS = {
    customer_service:     'Customer Service',
    quality_of_cars:      'Quality of Cars',
    value_added_services: 'Value Added Services',
    pricing_experience:   'Pricing & Valuation',
    pickup_process:       'Pickup & Paperwork',
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 560 }}>
        <div style={modalHeaderStyle}>
          <h2 style={{ margin: 0, fontSize: 17, color: '#003566' }}>
            Review #{review.id} — <StatusBadge status={review.status} />
          </h2>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        <div style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div style={{ background: '#dc354515', color: '#dc3545', padding: '8px 14px', borderRadius: 8, fontSize: 13 }}>
              ⚠️ {error}
            </div>
          )}

          {/* Customer info */}
          <Section title="Customer">
            <InfoRow label="Name"  value={review.customer_name || review.user?.name || 'N/A'} />
            <InfoRow label="Phone" value={review.customer_phone || 'N/A'} />
            <InfoRow label="Type"  value={<span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{review.review_type || 'N/A'}</span>} />
            {review.car && (
              <InfoRow label="Car" value={`${review.car.brand} ${review.car.model} (ID: ${review.car.id})`} />
            )}
          </Section>

          {/* Review content */}
          <Section title="Review Content">
            <InfoRow label="Rating"   value={<Stars rating={review.rating} />} />
            {review.category && (
              <InfoRow label="Category" value={CATEGORY_LABELS[review.category] || review.category} />
            )}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: 600 }}>Comment</div>
              <div style={{
                background: '#f8f9ff', border: '1px solid #e0e0f0',
                borderRadius: 8, padding: 12, fontSize: 13, color: '#333', lineHeight: 1.6,
                fontStyle: review.comment ? 'normal' : 'italic',
              }}>
                {review.comment || <span style={{ color: '#aaa' }}>Not yet submitted by customer</span>}
              </div>
            </div>
          </Section>

          {/* Timeline */}
          <Section title="Timeline">
            <InfoRow label="Link Sent"      value={review.token_sent_at  ? new Date(review.token_sent_at).toLocaleString('en-IN')  : '—'} />
            <InfoRow label="Form Submitted" value={review.token_used_at  ? new Date(review.token_used_at).toLocaleString('en-IN')  : '—'} />
            <InfoRow label="Approved At"    value={review.approved_at    ? new Date(review.approved_at).toLocaleString('en-IN')    : '—'} />
          </Section>

          {/* Admin note + actions — only for pending reviews that have content */}
          {review.status === 'pending' && review.comment && (
            <Section title="Admin Action">
              <label style={labelStyle}>Internal Note (optional)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 72, resize: 'vertical', marginTop: 4 }}
                placeholder="e.g. Verified customer, looks genuine"
                value={note} onChange={e => setNote(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button
                  onClick={handleApprove}
                  disabled={busy}
                  style={{ ...primaryBtn, background: '#28a745', flex: 1, opacity: busy ? 0.7 : 1 }}
                >
                  ✅ Approve & Publish
                </button>
                <button
                  onClick={handleReject}
                  disabled={busy}
                  style={{ ...primaryBtn, background: '#dc3545', flex: 1, opacity: busy ? 0.7 : 1 }}
                >
                  ✕ Reject
                </button>
              </div>
            </Section>
          )}

          {review.admin_note && (
            <Section title="Admin Note">
              <div style={{ fontSize: 13, color: '#555', background: '#fffbe6', borderRadius: 8, padding: 10 }}>
                {review.admin_note}
              </div>
            </Section>
          )}
        </div>

        <div style={{ padding: '12px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Small helpers ────────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div>
    <div style={{
      fontSize: 10, fontWeight: 700, color: '#003566', textTransform: 'uppercase',
      letterSpacing: 1, borderBottom: '1px solid #eee', paddingBottom: 4, marginBottom: 10
    }}>{title}</div>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f5f5f5' }}>
    <span style={{ fontSize: 12, color: '#888', minWidth: 120 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#333', fontWeight: 500, textAlign: 'right' }}>{value || <span style={{ color: '#ccc' }}>—</span>}</span>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const ReviewsManagement = () => {
  const [reviews, setReviews]     = useState([]);
  const [counts, setCounts]       = useState({});
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('all');
  const [typeFilter, setType]     = useState('all');
  const [search, setSearch]       = useState('');
  const [showSendModal, setSend]  = useState(false);
  const [detailReview, setDetail] = useState(null);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (statusFilter !== 'all') filters.status      = statusFilter;
      if (typeFilter   !== 'all') filters.review_type = typeFilter;

      const data = await adminFetchReviews(filters);
      setReviews(data.reviews || []);
      setCounts(data.counts  || {});
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await adminDeleteReview(id);
      showToast('Review deleted.');
      loadReviews();
    } catch (e) { showToast(e.message, 'error'); }
  };

  // Client-side search
  const displayed = reviews.filter(r => {
    const q = search.toLowerCase();
    return (
      !q ||
      r.customer_name?.toLowerCase().includes(q) ||
      r.customer_phone?.includes(q) ||
      r.comment?.toLowerCase().includes(q) ||
      r.user?.name?.toLowerCase().includes(q)
    );
  });

  const CATEGORY_LABELS = {
    customer_service:     'Customer Service',
    quality_of_cars:      'Quality of Cars',
    value_added_services: 'Value Added Services',
    pricing_experience:   'Pricing & Valuation',
    pickup_process:       'Pickup & Paperwork',
  };

  return (
    <div className="reviews-management" style={{ width: '100%' }}>

      {/* Toast */}
      {toast && (
        <div className={`admin-toast ${toast.type}`}>{toast.msg}</div>
      )}

      {/* Send modal */}
      {showSendModal && (
        <SendLinkModal
          onClose={() => setSend(false)}
          onSent={() => { loadReviews(); }}
        />
      )}

      {/* Detail / approve modal */}
      {detailReview && (
        <ReviewDetailModal
          review={detailReview}
          onClose={() => setDetail(null)}
          onAction={() => { loadReviews(); showToast('Review updated.'); }}
        />
      )}

      {/* Header */}
      <div className="management-header">
        <div>
          <h1>Reviews Management</h1>
          <p>Send review links, moderate submissions, and publish to the frontend.</p>
        </div>
        <button onClick={() => setSend(true)} style={primaryBtn}>
          📲 Send Review Link
        </button>
      </div>

      {/* Stat counters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { label: 'Total',    val: counts.all      || 0, color: '#003566' },
          { label: 'Pending',  val: counts.pending  || 0, color: '#856404' },
          { label: 'Approved', val: counts.approved || 0, color: '#28a745' },
          { label: 'Rejected', val: counts.rejected || 0, color: '#dc3545' },
        ].map(c => (
          <div key={c.label} style={{
            background: '#fff', border: `2px solid ${c.color}30`,
            borderRadius: 10, padding: '10px 18px', minWidth: 100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.val}</div>
            <div style={{ fontSize: 11, color: '#666', fontWeight: 600 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="management-filters">
        <div className="search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text" placeholder="Search by name, phone, or comment…"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-tabs" style={{ marginTop: 8 }}>
          {/* Status filters */}
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <button
              key={s}
              className={`filter-tab ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatus(s)}
            >
              {s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && <span className="filter-count">{counts[s] || 0}</span>}
            </button>
          ))}

          <div style={{ width: 1, background: '#e0e0e0', alignSelf: 'stretch', margin: '0 4px' }} />

          {/* Type filters */}
          {['all', 'buyer', 'seller'].map(t => (
            <button
              key={t}
              className={`filter-tab ${typeFilter === t ? 'active' : ''}`}
              onClick={() => setType(t)}
            >
              {t === 'all' ? 'All Types' : t === 'buyer' ? '🛒 Buyer' : '🚗 Seller'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="management-table-wrapper">
        <div className="management-table">
          {loading ? (
            <div className="management-loading">
              <div className="spinner-large" />
              <p>Loading reviews…</p>
            </div>
          ) : (
            <table style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Type</th>
                  <th>Rating</th>
                  <th>Comment</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Link Sent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                      No reviews found.
                    </td>
                  </tr>
                ) : displayed.map(r => {
                  const name = r.customer_name || r.user?.name || 'N/A';
                  const initials = name.charAt(0).toUpperCase();
                  const isSubmitted = !!r.comment;
                  const isPending   = r.status === 'pending' && isSubmitted;

                  return (
                    <tr key={r.id} style={isPending ? { background: '#fffbe6' } : {}}>
                      {/* Customer */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: isPending ? '#856404' : r.status === 'approved' ? '#28a745' : '#6c757d',
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>{name}</div>
                            <div style={{ fontSize: 10, color: '#888' }}>#{r.id}</div>
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td style={{ fontSize: 12 }}>{r.customer_phone || '—'}</td>

                      {/* Type */}
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px',
                          borderRadius: 8, textTransform: 'capitalize',
                          background: r.review_type === 'buyer' ? '#17a2b820' : '#6f42c120',
                          color:       r.review_type === 'buyer' ? '#17a2b8'   : '#6f42c1',
                        }}>
                          {r.review_type || '—'}
                        </span>
                      </td>

                      {/* Rating */}
                      <td><Stars rating={r.rating} /></td>

                      {/* Comment */}
                      <td style={{ maxWidth: 200 }}>
                        {isSubmitted ? (
                          <div style={{
                            fontSize: 11, color: '#333',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 180,
                          }}>
                            {r.comment}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>
                            Awaiting customer…
                          </span>
                        )}
                      </td>

                      {/* Category */}
                      <td style={{ fontSize: 11 }}>
                        {r.category ? CATEGORY_LABELS[r.category] || r.category : '—'}
                      </td>

                      {/* Status */}
                      <td><StatusBadge status={r.status} /></td>

                      {/* Link Sent */}
                      <td style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>
                        {r.token_sent_at
                          ? new Date(r.token_sent_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                          : '—'}
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-view"
                            onClick={() => setDetail(r)}
                            title="View details"
                          >👁</button>

                          {isPending && (
                            <>
                              <button
                                className="btn-approve"
                                onClick={async () => {
                                  try { await adminApproveReview(r.id); loadReviews(); showToast('Approved!'); }
                                  catch (e) { showToast(e.message, 'error'); }
                                }}
                                title="Approve"
                              >✓</button>
                              <button
                                className="btn-reject"
                                onClick={async () => {
                                  try { await adminRejectReview(r.id); loadReviews(); showToast('Rejected.'); }
                                  catch (e) { showToast(e.message, 'error'); }
                                }}
                                title="Reject"
                              >✕</button>
                            </>
                          )}

                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(r.id)}
                            title="Delete"
                          >🗑</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Inline styles ─────────────────────────────────────────────────────────────
const overlayStyle    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 };
const modalStyle      = { background: '#fff', borderRadius: 14, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' };
const modalHeaderStyle= { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #eee', background: '#f8f9ff' };
const closeBtnStyle   = { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#666' };
const primaryBtn      = { background: '#FFD60A', color: '#003566', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 };
const secondaryBtn    = { background: '#f1f3f5', color: '#555', border: '1px solid #ddd', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const labelStyle      = { fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 };
const inputStyle      = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };

export default ReviewsManagement;